/**
 * Multi-site poker hand-history (.txt) parser.
 *
 * Supports the common "PokerStars-style" hand history dialects used by most
 * major rooms (GGPoker/PokerCraft, PokerStars, ACR/WPN, CoinPoker, Unibet,
 * Winamax, WPT Global) as well as the older "888/iPoker/Party" bracket-amount
 * style (888poker, iPoker, PartyPoker). Sites differ in header layout, colon
 * usage after player names, bracket-vs-plain amounts, and currency symbol
 * placement ($ prefix, EUR/USDT suffix, etc.) - all of that is normalised
 * below so the rest of the app only ever sees one canonical hand shape.
 *
 * Applies the eligibility filters for GTO analysis:
 *   - NL Hold'em, 6-max cash only
 *   - Hero must be dealt cards
 *   - Hero stack must be 80-120bb at the start of the hand
 * Ineligible hands are returned with a human-readable skip reason.
 */

// Acting-order position lists (must match the Trainer/Solver conventions).
export const PREFLOP_ORDER = ["lj", "hj", "co", "btn", "sb", "bb"];
export const POSTFLOP_ORDER = ["sb", "bb", "lj", "hj", "co", "btn"];
export const POSITION_LABELS = { lj: "LJ", hj: "HJ", co: "CO", btn: "BTN", sb: "SB", bb: "BB" };

// ---- currency-agnostic amount matching -----------------------------------
// Handles "$1.50", "1.50", "€1.50", "1.50€", "1.50 USDT", "[$1.50]", etc.
const CCY_PREFIX = "(?:[€$£]\\s?)?";
const CCY_SUFFIX = "(?:\\s?(?:USD|USDT|EUR|GBP|€|\\$|£))?";
const AMOUNT_SRC = `${CCY_PREFIX}(\\d[\\d,]*\\.?\\d*)${CCY_SUFFIX}`;

function money(txt) {
  return Number(String(txt || "0").replace(/,/g, "")) || 0;
}

/** Pulls every bare number out of a fragment of text, ignoring currency symbols/codes. */
function extractAllAmounts(str) {
  const matches = String(str || "").match(/\d[\d,]*\.?\d*/g) || [];
  return matches.map(money);
}

function normalizeCard(card) {
  const c = String(card || "").trim();
  if (c.length < 2) return null;
  return `${c[0].toUpperCase()}${c[1].toLowerCase()}`;
}

/** Extracts cards from one or more [..] groups; tolerates comma- or space-separated cards. */
function cardsFromBracket(segment) {
  const matches = String(segment || "").match(/\[([^\]]*)\]/g) || [];
  const cards = [];
  for (const m of matches) {
    const inner = m.replace(/[[\]]/g, "").replace(/,/g, " ").trim();
    if (!inner) continue;
    for (const c of inner.split(/\s+/)) {
      const norm = normalizeCard(c);
      if (norm) cards.push(norm);
    }
  }
  return cards;
}

/** Best-effort date parsing across the handful of date styles seen in the wild. Cosmetic only. */
function parseDateToIso(txt) {
  const t = String(txt || "").trim();
  // 2022/12/07 01:06:06  or  2026-07-24 20:24:10
  let m = t.match(/(\d{4})[/-](\d{2})[/-](\d{2})[\sT](\d{2}):(\d{2}):(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  }
  // 24 07 2026 20:36:10  (day month year)
  m = t.match(/\b(\d{2})\s+(\d{2})\s+(\d{4})\s+(\d{2}):(\d{2}):(\d{2})\b/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4]), Number(m[5]), Number(m[6]));
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  }
  return null;
}

function isNlHoldem(text) {
  const t = String(text || "");
  if (!/hold.?em/i.test(t)) return false;
  if (/pot limit|fixed limit|\bplo\b/i.test(t)) return false;
  return true;
}

// ---- hand-start detection (per-site header opener) -----------------------
const HAND_START_PATTERNS = [
  /^\*{3,5}.*Hand History for Game\s+(\S+)/i, // 888poker / PartyPoker
  /^(?:PokerStars|CoinPoker|Unibet Poker|WPT Global|Poker)\s+Hand\s+#([^\s:]+)/i, // GG / Stars / Coin / Unibet / WPTG
  /^Hand\s*#(\S+)\s*-/i, // ACR/WPN
  /^Game\s*#(\S+)\s*starts\.?/i, // iPoker
  /^Winamax Poker.*HandId:\s*#(\S+)/i, // Winamax
];

function matchHandStart(line) {
  for (const re of HAND_START_PATTERNS) {
    const m = line.match(re);
    if (m) return m;
  }
  return null;
}

/** Split the raw file into per-hand text blocks, recognising any supported site's header line. */
function splitIntoHandBlocks(fileText) {
  const lines = String(fileText || "").split(/\r?\n/);
  const blocks = [];
  let current = null;
  for (const line of lines) {
    if (matchHandStart(line)) {
      if (current?.length) blocks.push(current);
      current = [line];
    } else if (current) {
      current.push(line);
    }
  }
  if (current?.length) blocks.push(current);
  return blocks;
}

/** Assign table positions from the button seat. Requires exactly 6 seats. */
function assignPositions(seats, buttonSeatNo) {
  const ordered = [...seats].sort((a, b) => a.seatNo - b.seatNo);
  const btnIdx = ordered.findIndex((s) => s.seatNo === buttonSeatNo);
  if (btnIdx < 0 || ordered.length !== 6) return null;
  // Clockwise from the button: SB, BB, LJ (UTG), HJ, CO, back to BTN.
  const posOrder = ["sb", "bb", "lj", "hj", "co", "btn"];
  const byName = {};
  for (let i = 1; i <= 6; i++) {
    const seat = ordered[(btnIdx + i) % 6];
    byName[seat.name] = posOrder[(i - 1) % 6];
  }
  byName[ordered[btnIdx].name] = "btn";
  return byName;
}

// ---- per-line regexes, tolerant of every supported site's dialect --------
const SEAT_RE = new RegExp(`^Seat (\\d+):\\s*(.+?)\\s*\\(\\s*${AMOUNT_SRC}(?:\\s+in chips)?\\s*\\)`, "i");
const BUTTON_SEAT_RE = /Seat\s*#?(\d+)\s+is the button/i;
const STAKES_RE = new RegExp(`${AMOUNT_SRC}\\s*/\\s*${AMOUNT_SRC}`);
const TABLE_NAME_RE = /Table:?\s*'([^']*)'/i;
const POST_RE = new RegExp(
  `^(.+?):?\\s+posts\\s+(?:the\\s+)?(small blind|big blind|ante|straddle|missed blind)\\s*\\[?\\s*${AMOUNT_SRC}`,
  "i"
);
const DEALT_RE = /^Dealt to (.+?)\s*(\[[^\]]*\])?\s*$/;
const ACTION_LINE_RE = /^(.+?):?\s+(folds|checks|calls|bets|raises)\b(.*)$/i;
const STREET_RE = /^\*{2,3}\s*(?:Dealing\s+)?(FLOP|TURN|RIVER)\s*\*{2,3}(.*)$/i;
const RIT_STREET_RE = /^\*{2,3}\s*(FIRST|SECOND)\s+(FLOP|TURN|RIVER)\s*\*{2,3}(.*)$/i;
const PREFLOP_MARKER_RE = /^\*{2,3}\s*(?:Dealing down cards|HOLE CARDS|PRE-?FLOP)\s*\*{2,3}/i;
const SHOWDOWN_MARKER_RE = /^\*{2,3}\s*(?:FIRST|SECOND)?\s*SHOW\s*DOWN\s*\*{2,3}/i;
const SUMMARY_MARKER_RE = /^\*{2,3}\s*SUMMARY\s*\*{2,3}/i;
const UNCALLED_RE = new RegExp(`^Uncalled bet\\s*\\(\\s*${AMOUNT_SRC}\\s*\\)\\s*returned to (.+)$`, "i");
const COLLECTED_RE = new RegExp(`^(.+?)\\s+(?:collected|wins)\\s*\\[?\\s*${AMOUNT_SRC}\\s*\\]?`, "i");
const SHOWS_RE = /^(.+?):?\s+shows\s*(\[[^\]]*\])/i;
const TOTAL_POT_RE = new RegExp(`^Total pot\\s*${AMOUNT_SRC}`, "i");
const CASHOUT_RECEIVE_RE = new RegExp(`^(.+?):\\s*Receives Cashout\\s*\\(\\s*${AMOUNT_SRC}\\s*\\)`, "i");
const CASHOUT_RISK_RE = new RegExp(`^(.+?):\\s*Pays Cashout Risk\\s*\\(\\s*${AMOUNT_SRC}\\s*\\)`, "i");

function parseOneHand(lines) {
  const startM = matchHandStart(lines[0]);
  const handId = (startM && startM[1]) || `unknown-${Math.random().toString(36).slice(2, 10)}`;

  // Header lines run from the block opener up to (not including) the first "Seat N: Name (stack)" line.
  let seatStartIdx = lines.length;
  for (let i = 0; i < lines.length; i++) {
    if (SEAT_RE.test(lines[i].trim())) {
      seatStartIdx = i;
      break;
    }
  }
  const headerLines = lines.slice(0, seatStartIdx);
  const headerText = headerLines.join(" \n ");

  let sbAmount = 0;
  let bbAmount = 0;
  const stakesM = headerText.match(STAKES_RE);
  if (stakesM) {
    sbAmount = money(stakesM[1]);
    bbAmount = money(stakesM[2]);
  }
  let playedAt = null;
  for (const hl of headerLines) {
    const iso = parseDateToIso(hl);
    if (iso) {
      playedAt = iso;
      break;
    }
  }
  const tableNameM = headerText.match(TABLE_NAME_RE);
  const buttonM = headerText.match(BUTTON_SEAT_RE);

  const hand = {
    handId,
    gameName: headerText.slice(0, 160),
    isNlHoldem: isNlHoldem(headerText),
    sbAmount,
    bbAmount,
    playedAt,
    tableName: tableNameM ? tableNameM[1] : null,
    tableFormat: null,
    buttonSeat: null,
    buttonSeatFromText: buttonM ? Number(buttonM[1]) : null,
    seats: [],
    positions: null,
    heroName: null,
    heroCards: null,
    boardCards: [],
    ranItTwice: false,
    hasAnteOrStraddle: false,
    streets: { preflop: [], flop: [], turn: [], river: [] },
    investedByName: {},
    collectedByName: {},
    uncalledByName: {},
    showdown: [],
    totalPot: 0,
    heroCashoutReceived: 0,
    heroCashoutRiskPaid: 0,
  };

  let phase = "setup"; // setup -> preflop -> flop -> turn -> river -> done
  let inSummary = false;
  let sbPosterSeat = null;
  const streetInvested = {}; // per-player invested on the current street

  const addInvested = (name, amt) => {
    hand.investedByName[name] = Number(((hand.investedByName[name] || 0) + amt).toFixed(4));
  };
  const resetStreetInvested = () => {
    for (const k of Object.keys(streetInvested)) streetInvested[k] = 0;
  };

  for (let li = 1; li < lines.length; li++) {
    const line = lines[li].trim();
    if (!line) continue;

    if (SHOWDOWN_MARKER_RE.test(line)) {
      phase = "done";
      continue;
    }

    if (SUMMARY_MARKER_RE.test(line)) {
      inSummary = true;
      continue;
    }
    if (inSummary) {
      const potM = line.match(TOTAL_POT_RE);
      if (potM) hand.totalPot = money(potM[1]);
      continue;
    }

    const seatM = phase === "setup" ? line.match(SEAT_RE) : null;
    if (seatM) {
      hand.seats.push({ seatNo: Number(seatM[1]), name: seatM[2].trim(), stack: money(seatM[3]) });
      continue;
    }

    const postM = line.match(POST_RE);
    if (postM) {
      const name = postM[1].trim();
      const kind = postM[2].toLowerCase();
      const amt = money(postM[3]);
      if (kind === "small blind") {
        if (amt > 0) hand.sbAmount = amt;
        if (sbPosterSeat == null) {
          const seatRec = hand.seats.find((s) => s.name === name);
          if (seatRec) sbPosterSeat = seatRec.seatNo;
        }
      } else if (kind === "big blind" && amt > 0) {
        hand.bbAmount = amt;
      } else if (kind === "ante" || kind === "straddle" || kind === "missed blind") {
        hand.hasAnteOrStraddle = true;
      }
      addInvested(name, amt);
      streetInvested[name] = (streetInvested[name] || 0) + amt;
      continue;
    }

    if (PREFLOP_MARKER_RE.test(line)) {
      phase = "preflop";
      continue;
    }

    const ritM = line.match(RIT_STREET_RE);
    if (ritM) {
      // Run-it-twice: all decisions are already in; track the FIRST board for display.
      hand.ranItTwice = true;
      phase = "done";
      if (ritM[1].toUpperCase() === "FIRST") {
        const cards = cardsFromBracket(ritM[3]);
        if (ritM[2].toUpperCase() === "FLOP") hand.boardCards = cards.slice(0, 3);
        else if (cards.length) hand.boardCards = [...hand.boardCards, cards[cards.length - 1]];
      }
      continue;
    }

    const streetM = line.match(STREET_RE);
    if (streetM) {
      const streetName = streetM[1].toUpperCase();
      const cards = cardsFromBracket(streetM[2]);
      if (streetName === "FLOP") {
        phase = "flop";
        hand.boardCards = cards.slice(0, 3);
      } else if (streetName === "TURN") {
        phase = "turn";
        if (cards.length) hand.boardCards = [...hand.boardCards.slice(0, 3), cards[cards.length - 1]];
      } else {
        phase = "river";
        if (cards.length) hand.boardCards = [...hand.boardCards.slice(0, 4), cards[cards.length - 1]];
      }
      resetStreetInvested();
      continue;
    }

    const dealtM = line.match(DEALT_RE);
    if (dealtM) {
      const cards = dealtM[2] ? cardsFromBracket(dealtM[2]) : [];
      if (cards.length >= 2) {
        hand.heroName = dealtM[1].trim();
        hand.heroCards = [cards[0], cards[1]];
      }
      continue;
    }

    const uncalledM = line.match(UNCALLED_RE);
    if (uncalledM) {
      const name = uncalledM[2].trim();
      const amt = money(uncalledM[1]);
      hand.uncalledByName[name] = (hand.uncalledByName[name] || 0) + amt;
      continue;
    }

    const collectedM = line.match(COLLECTED_RE);
    if (collectedM) {
      const name = collectedM[1].trim();
      hand.collectedByName[name] = (hand.collectedByName[name] || 0) + money(collectedM[2]);
      continue;
    }

    const showsM = line.match(SHOWS_RE);
    if (showsM) {
      const cards = cardsFromBracket(showsM[2]);
      if (cards.length >= 2) hand.showdown.push({ name: showsM[1].trim(), cards: [cards[0], cards[1]] });
      continue;
    }

    const cashRecvM = line.match(CASHOUT_RECEIVE_RE);
    if (cashRecvM) {
      if (cashRecvM[1].trim() === hand.heroName) hand.heroCashoutReceived += money(cashRecvM[2]);
      continue;
    }
    const cashRiskM = line.match(CASHOUT_RISK_RE);
    if (cashRiskM) {
      if (cashRiskM[1].trim() === hand.heroName) hand.heroCashoutRiskPaid += money(cashRiskM[2]);
      continue;
    }

    const actionM = line.match(ACTION_LINE_RE);
    if (actionM && (phase === "preflop" || phase === "flop" || phase === "turn" || phase === "river")) {
      const name = actionM[1].trim();
      const verb = actionM[2].toLowerCase();
      const rest = actionM[3] || "";
      const allIn = /all-?in/i.test(rest);
      const nums = extractAllAmounts(rest);
      const entry = { name, allIn };
      const before = Number(streetInvested[name] || 0);

      if (verb === "folds") {
        entry.type = "fold";
        entry.contribution = 0;
      } else if (verb === "checks") {
        entry.type = "check";
        entry.contribution = 0;
      } else if (verb === "calls") {
        const amount = nums[0] || 0;
        entry.type = "call";
        entry.amount = amount;
        entry.contribution = amount;
        streetInvested[name] = before + amount;
        addInvested(name, amount);
      } else if (verb === "bets") {
        const amount = nums[0] || 0;
        entry.type = "bet";
        entry.amount = amount;
        entry.streetTotal = before + amount;
        entry.contribution = amount;
        streetInvested[name] = before + amount;
        addInvested(name, amount);
      } else {
        // "raises X to Y", "raises to Y" or bracket-only "raises [Y]" (Y = total) - the
        // last number on the line is always the post-raise total across every site dialect.
        const total = nums.length ? nums[nums.length - 1] : before;
        entry.type = "raise";
        entry.streetTotal = total;
        const added = Math.max(0, total - before);
        entry.amount = added;
        entry.contribution = added;
        streetInvested[name] = total;
        addInvested(name, added);
      }
      entry.investedAfter = Number((streetInvested[name] || 0).toFixed(4));
      hand.streets[phase].push(entry);
      continue;
    }
  }

  // Button seat: always derive from the small-blind poster (SB sits directly left of the
  // button), which is the one piece of information every site states unambiguously via the
  // actual blind posts - more reliable than "Seat # is the button" headers, which some sites
  // format inconsistently. Fall back to the header text only if blinds couldn't be matched.
  if (sbPosterSeat != null && hand.seats.length === 6) {
    hand.buttonSeat = ((sbPosterSeat - 2 + 6) % 6) + 1;
  } else if (hand.buttonSeatFromText != null) {
    hand.buttonSeat = hand.buttonSeatFromText;
  }
  hand.tableFormat = hand.seats.length === 6 ? "6-max" : null;
  hand.positions = hand.seats.length === 6 && hand.buttonSeat != null ? assignPositions(hand.seats, hand.buttonSeat) : null;
  return { hand };
}

/** Eligibility check. Returns null when analyzable, else a skip reason string. */
export function eligibilityReason(hand) {
  if (!hand) return "Unparseable hand";
  if (!hand.isNlHoldem) return "Not NL Hold'em";
  if ((hand.seats || []).length !== 6) return "Table was not 6-handed";
  if (!hand.positions) return "Could not resolve table positions";
  if (hand.hasAnteOrStraddle) return "Antes/straddles not supported";
  if (!hand.heroName || !hand.heroCards) return "Hero cards not found";
  if (!(hand.bbAmount > 0)) return "Missing blind info";
  const heroSeat = (hand.seats || []).find((s) => s.name === hand.heroName);
  if (!heroSeat) return "Hero seat not found";
  const stackBb = heroSeat.stack / hand.bbAmount;
  if (stackBb < 80 || stackBb > 120) return `Hero stack ${stackBb.toFixed(0)}bb (needs 80-120bb)`;
  return null;
}

/**
 * Parse a full multi-hand hand-history export file (any supported site).
 * Returns { eligible: [hand], skipped: [{handId, reason}], parseErrors: number }
 */
export function parseGgFile(fileText) {
  const blocks = splitIntoHandBlocks(fileText);
  const eligible = [];
  const skipped = [];
  let parseErrors = 0;
  const seenIds = new Set();
  for (const block of blocks) {
    let parsed;
    try {
      parsed = parseOneHand(block);
    } catch (err) {
      parseErrors += 1;
      continue;
    }
    if (parsed.error || !parsed.hand) {
      parseErrors += 1;
      continue;
    }
    const hand = parsed.hand;
    if (seenIds.has(hand.handId)) continue;
    seenIds.add(hand.handId);
    const reason = eligibilityReason(hand);
    if (reason) skipped.push({ handId: hand.handId, reason });
    else eligible.push(hand);
  }
  return { eligible, skipped, parseErrors, totalHands: blocks.length };
}
